
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface BannerItem {
  id: string;
  type: 'announcement' | 'ad' | 'event' | 'promo';
  title: string;
  subtitle: string;
  cta: string;
  ctaLink: string;
  bgImage: string;
  gradient: string;
  badge?: string;
  badgeColor?: string;
}

const globalBanners: BannerItem[] = [
  {
    id: 'ann-1',
    type: 'announcement',
    title: 'Community Guidelines Updated',
    subtitle: 'Review the latest rules to keep our forum welcoming',
    cta: 'Read Now',
    ctaLink: '/forum?tab=general',
    bgImage: 'https://public.readdy.ai/ai/img_res/b2424bc1b5b35fe94d3b28020202ff61.jpg',
    gradient: 'from-teal-700/80 to-emerald-700/80',
    badge: 'Announcement',
    badgeColor: 'bg-teal-400/30 text-teal-100'
  },
  {
    id: 'ad-1',
    type: 'ad',
    title: 'Create Your AI Drama',
    subtitle: 'Start your creative journey with powerful AI tools',
    cta: 'Get Started',
    ctaLink: '/dramas',
    bgImage: 'https://readdy.ai/api/search-image?query=Abstract%20digital%20art%20background%20with%20flowing%20warm%20coral%20and%20amber%20gradients%20cinematic%20film%20production%20theme%20modern%20minimalist%20design%20with%20subtle%20geometric%20patterns%20professional%20creative%20atmosphere&width=400&height=220&seq=forumbanner2&orientation=landscape',
    gradient: 'from-amber-700/80 to-orange-700/80',
    badge: 'Ad',
    badgeColor: 'bg-black/30 text-white/80'
  },
  {
    id: 'event-1',
    type: 'event',
    title: 'Weekly Drama Challenge',
    subtitle: 'Create a 60-second AI episode & win prizes',
    cta: 'Join Now',
    ctaLink: '/forum?tab=ai-dramas',
    bgImage: 'https://readdy.ai/api/search-image?query=Abstract%20creative%20background%20with%20rose%20pink%20and%20warm%20magenta%20gradients%20artistic%20contest%20competition%20theme%20modern%20elegant%20design%20with%20confetti%20light%20effects%20celebration%20atmosphere&width=400&height=220&seq=forumbanner3&orientation=landscape',
    gradient: 'from-rose-700/80 to-pink-700/80',
    badge: 'Event',
    badgeColor: 'bg-rose-400/30 text-rose-100'
  },
];

const categoryBanners: Record<string, BannerItem[]> = {
  general: [
    {
      id: 'gen-promo-1',
      type: 'promo',
      title: 'Start a Discussion',
      subtitle: 'Share your thoughts and connect with drama fans',
      cta: 'New Post',
      ctaLink: '#',
      bgImage: 'https://readdy.ai/api/search-image?query=Abstract%20background%20with%20soft%20blue%20and%20cyan%20gradients%20modern%20community%20discussion%20theme%20minimalist%20design%20with%20speech%20bubble%20patterns%20clean%20professional%20atmosphere&width=400&height=220&seq=forumbannergen1&orientation=landscape',
      gradient: 'from-blue-700/80 to-cyan-700/80',
      badge: 'Featured',
      badgeColor: 'bg-blue-400/30 text-blue-100'
    },
  ],
  'ai-dramas': [
    {
      id: 'ai-promo-1',
      type: 'promo',
      title: 'AI Tools Comparison 2025',
      subtitle: 'Sora vs Runway vs Kling — find the best fit',
      cta: 'Compare',
      ctaLink: '/forum?tab=ai-dramas',
      bgImage: 'https://readdy.ai/api/search-image?query=Abstract%20futuristic%20background%20with%20warm%20orange%20and%20amber%20gradients%20AI%20technology%20robot%20theme%20modern%20minimalist%20design%20with%20circuit%20patterns%20creative%20tech%20atmosphere&width=400&height=220&seq=forumbannerai1&orientation=landscape',
      gradient: 'from-orange-700/80 to-amber-700/80',
      badge: 'Hot',
      badgeColor: 'bg-orange-400/30 text-orange-100'
    },
  ],
  reviews: [
    {
      id: 'rev-promo-1',
      type: 'promo',
      title: 'Top Rated This Week',
      subtitle: 'See what the community is loving right now',
      cta: 'View Ratings',
      ctaLink: '/forum?tab=reviews',
      bgImage: 'https://readdy.ai/api/search-image?query=Abstract%20elegant%20background%20with%20warm%20golden%20yellow%20and%20amber%20gradients%20star%20rating%20review%20theme%20modern%20minimalist%20design%20with%20star%20patterns%20premium%20quality%20atmosphere&width=400&height=220&seq=forumbannerrev1&orientation=landscape',
      gradient: 'from-yellow-700/80 to-amber-700/80',
      badge: 'Trending',
      badgeColor: 'bg-yellow-400/30 text-yellow-100'
    },
  ],
  creators: [
    {
      id: 'cre-promo-1',
      type: 'promo',
      title: 'Creator Spotlight Program',
      subtitle: 'Apply to be featured and grow your audience',
      cta: 'Apply',
      ctaLink: '/creators',
      bgImage: 'https://readdy.ai/api/search-image?query=Abstract%20creative%20background%20with%20soft%20purple%20and%20pink%20gradients%20spotlight%20stage%20theme%20modern%20minimalist%20design%20with%20light%20beam%20patterns%20artistic%20professional%20atmosphere&width=400&height=220&seq=forumbannercre1&orientation=landscape',
      gradient: 'from-purple-700/80 to-pink-700/80',
      badge: 'Open',
      badgeColor: 'bg-purple-400/30 text-purple-100'
    },
  ],
  recommendations: [
    {
      id: 'rec-promo-1',
      type: 'promo',
      title: 'Curated Picks of the Month',
      subtitle: 'Hand-picked dramas by our community experts',
      cta: 'Explore',
      ctaLink: '/forum?tab=recommendations',
      bgImage: 'https://readdy.ai/api/search-image?query=Abstract%20fresh%20background%20with%20soft%20green%20and%20emerald%20gradients%20curated%20collection%20theme%20modern%20minimalist%20design%20with%20leaf%20patterns%20welcoming%20discovery%20atmosphere&width=400&height=220&seq=forumbannerrec1&orientation=landscape',
      gradient: 'from-green-700/80 to-emerald-700/80',
      badge: 'Curated',
      badgeColor: 'bg-green-400/30 text-green-100'
    },
  ],
  news: [
    {
      id: 'news-promo-1',
      type: 'promo',
      title: 'Breaking News Alerts',
      subtitle: 'Never miss major industry announcements',
      cta: 'Subscribe',
      ctaLink: '#',
      bgImage: 'https://readdy.ai/api/search-image?query=Abstract%20dynamic%20background%20with%20warm%20red%20and%20rose%20gradients%20breaking%20news%20alert%20theme%20modern%20minimalist%20design%20with%20signal%20wave%20patterns%20urgent%20professional%20atmosphere&width=400&height=220&seq=forumbannernews1&orientation=landscape',
      gradient: 'from-red-700/80 to-rose-700/80',
      badge: 'Live',
      badgeColor: 'bg-red-400/30 text-red-100'
    },
  ],
  'behind-scenes': [
    {
      id: 'bts-promo-1',
      type: 'promo',
      title: 'Exclusive BTS Content',
      subtitle: 'Go behind the camera with top productions',
      cta: 'Discover',
      ctaLink: '/forum?tab=behind-scenes',
      bgImage: 'https://readdy.ai/api/search-image?query=Abstract%20cinematic%20background%20with%20soft%20indigo%20and%20violet%20gradients%20behind%20the%20scenes%20film%20camera%20theme%20modern%20minimalist%20design%20with%20film%20strip%20patterns%20exclusive%20content%20atmosphere&width=400&height=220&seq=forumbannerbts1&orientation=landscape',
      gradient: 'from-indigo-700/80 to-violet-700/80',
      badge: 'Exclusive',
      badgeColor: 'bg-indigo-400/30 text-indigo-100'
    },
  ],
};

interface ForumBannerWidgetProps {
  activeTab: string;
}

export default function ForumBannerWidget({ activeTab }: ForumBannerWidgetProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const tabSpecific = categoryBanners[activeTab] || [];
  const allBanners = [...tabSpecific, ...globalBanners];

  useEffect(() => {
    setCurrentIndex(0);
  }, [activeTab]);

  const nextBanner = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % allBanners.length);
  }, [allBanners.length]);

  useEffect(() => {
    if (isHovered || allBanners.length <= 1) return;
    const timer = setInterval(nextBanner, 5000);
    return () => clearInterval(timer);
  }, [isHovered, nextBanner, allBanners.length]);

  const current = allBanners[currentIndex] || allBanners[0];
  if (!current) return null;

  return (
    <div
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link
        to={current.ctaLink}
        className="relative block h-[180px] overflow-hidden group"
      >
        <img
          src={current.bgImage}
          alt={current.title}
          className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className={`absolute inset-0 bg-gradient-to-br ${current.gradient} transition-opacity group-hover:opacity-90`} />

        <div className="relative z-10 h-full flex flex-col justify-between p-4">
          <div className="flex items-center justify-between">
            {current.badge && (
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${current.badgeColor}`}>
                {current.badge}
              </span>
            )}
          </div>

          <div>
            <h3 className="text-white font-bold text-base leading-tight mb-1">
              {current.title}
            </h3>
            <p className="text-white/85 text-xs leading-relaxed mb-3">
              {current.subtitle}
            </p>
            <span className="inline-block bg-white text-gray-800 text-xs font-semibold px-4 py-1.5 rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap">
              {current.cta}
            </span>
          </div>
        </div>
      </Link>

      {allBanners.length > 1 && (
        <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
          <button
            onClick={() => setCurrentIndex(prev => (prev - 1 + allBanners.length) % allBanners.length)}
            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-sm"></i>
          </button>
          <div className="flex items-center gap-1.5">
            {allBanners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                  currentIndex === index
                    ? 'bg-teal-500 w-4'
                    : 'bg-gray-300 hover:bg-gray-400 w-1.5'
                }`}
              />
            ))}
          </div>
          <button
            onClick={nextBanner}
            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-sm"></i>
          </button>
        </div>
      )}
    </div>
  );
}
